import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ClipboardList, Clock, Truck } from 'lucide-react';
import Input from '../../components/ui/Input';
import QuantityInput from '../../components/ui/QuantityInput';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import { isAfter, startOfDay, parseISO, format } from 'date-fns';
import { subscribeFactories } from '../../data/factoriesData';
import { useAuth } from '../../contexts/AuthContext';
import { Helmet } from 'react-helmet';

const generatePONumber = () => {
  const today = format(new Date(), 'yyyyMMdd');
  const key = `po_seq_${today}`;
  const current = parseInt(localStorage.getItem(key) || '0', 10) + 1;
  localStorage.setItem(key, String(current));
  const seq = String(current)?.padStart(3, '0');
  return `SLO${today}-${seq}`;
};

const savePendingOrder = (orderData) => {
  const existing = JSON.parse(localStorage.getItem('sugarHathi_pendingOrders') || '[]');
  existing?.unshift(orderData);
  localStorage.setItem('sugarHathi_pendingOrders', JSON.stringify(existing));
};

const isPincodeValid = (pincode) => /^\d{6}$/?.test(pincode);

// Factory data with location info
const factoryData = [
  { value: 'KSSK', label: 'KSSK – Kopargaon SSK', location: 'Kopargaon, Maharashtra', city: 'Nashik', pincode: '423601' },
  { value: 'PVSSK', label: 'PVSSK – Pravara VSK', location: 'Pravara, Maharashtra', city: 'Nashik', pincode: '413713' },
  { value: 'SCSSK', label: 'SCSSK – Shri Chhatrapati SSK', location: 'Aurangabad, Maharashtra', city: 'Aurangabad', pincode: '431001' },
  { value: 'VNSSK', label: 'VNSSK – Vitthalrao Nagar SSK', location: 'Solapur, Maharashtra', city: 'Solapur', pincode: '413004' },
  { value: 'MSSSK', label: 'MSSSK – Malegaon Sahakari SSK', location: 'Malegaon, Nashik', city: 'Nashik', pincode: '423203' },
  { value: 'BSSK', label: 'BSSK – Bhima SSK', location: 'Bhima, Pune', city: 'Pune', pincode: '412205' },
  { value: 'NSSSK', label: 'NSSSK – Nira Bhima SSK', location: 'Nira, Solapur', city: 'Solapur', pincode: '413102' },
  { value: 'RSSSK', label: 'RSSSK – Rajaram SSK', location: 'Rajaram, Kolhapur', city: 'Kolhapur', pincode: '416004' },
  { value: 'DSSSK', label: 'DSSSK – Datta SSK', location: 'Datta, Nashik', city: 'Nashik', pincode: '422401' },
  { value: 'WSSSK', label: 'WSSSK – Warana SSK', location: 'Warana, Kolhapur', city: 'Kolhapur', pincode: '416113' },
];

const RATE_PER_TRUCK_PER_KM = 12;

// Calculate rate per ton based on distance (₹2.8/ton/km base, min ₹1,100, max ₹1,450)
const calcRatePerTon = (distKm) => {
  if (!distKm) return null;
  const rate = Math.round(distKm * 2.8);
  return Math.min(Math.max(rate, 1100), 1450);
};

// Multi-destination Sugar + Truck section component
const SugarTruckSection = ({ factoryName, allFactories }) => {
  const [destinations, setDestinations] = useState([
    { id: 1, city: '', pincode: '', trucks: 1 }
  ]);

  const factory = (allFactories || factoryData)?.find(f => f?.value === factoryName);
  const fromLocation = factory?.location || '';
  const fromPincode = factory?.pincode || '';

  const addDestination = () => {
    setDestinations(prev => [...prev, { id: Date.now(), city: '', pincode: '', trucks: 1 }]);
  };

  const removeDestination = (id) => {
    if (destinations?.length <= 1) return;
    setDestinations(prev => prev?.filter(d => d?.id !== id));
  };

  const fetchDistanceForDest = async (id, destPin) => {
    if (!fromPincode || fromPincode?.length !== 6 || destPin?.length !== 6) return;
    setDestinations(prev => prev?.map(d => d?.id === id ? { ...d, distLoading: true, lockedDist: null } : d));
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const resp = await fetch(`${supabaseUrl}/functions/v1/calculate-route-distance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originPincode: fromPincode, destinationPincode: destPin }),
      });
      if (resp?.ok) {
        const data = await resp.json();
        setDestinations(prev => prev?.map(d => d?.id === id ? { ...d, lockedDist: data?.distanceKm, distLoading: false } : d));
      } else {
        setDestinations(prev => prev?.map(d => d?.id === id ? { ...d, lockedDist: null, distLoading: false } : d));
      }
    } catch {
      setDestinations(prev => prev?.map(d => d?.id === id ? { ...d, lockedDist: null, distLoading: false } : d));
    }
  };

  const updateDestination = (id, field, value) => {
    setDestinations(prev => prev?.map(d => {
      if (d?.id !== id) return d;
      const updated = { ...d, [field]: value };
      if (field === 'pincode') {
        const pin = value?.replace(/\D/g, '')?.substring(0, 6);
        updated.pincode = pin;
        updated.lockedDist = null;
        updated.distLoading = false;
        if (pin?.length === 6) fetchDistanceForDest(id, pin);
      }
      return updated;
    }));
  };

  const totalTrucks = destinations?.reduce((sum, d) => sum + (parseInt(d?.trucks) || 0), 0);

  const totalTransportCost = destinations?.reduce((sum, d) => {
    if (!d?.lockedDist || !d?.trucks) return sum;
    return sum + (d?.lockedDist * RATE_PER_TRUCK_PER_KM * (parseInt(d?.trucks) || 0));
  }, 0);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })?.format(amount);

  return (
    <div className="rounded-xl border p-5 space-y-4" style={{ backgroundColor: '#fffbeb', borderColor: '#fde68a' }}>
      <div className="flex items-center gap-2 mb-1">
        <Truck size={18} color="#d97706" />
        <h3 className="text-sm font-bold" style={{ color: '#92400e' }}>Sugar + Truck Booking</h3>
      </div>
      <p className="text-xs" style={{ color: '#9ca3af' }}>
        Book trucks along with your sugar order. Add multiple destinations with truck quantity, distance, and rate per ton.
      </p>
      {/* From (auto-filled) */}
      <div>
        <label className="text-sm font-medium block mb-1.5" style={{ color: '#374151' }}>
          From (Loading Factory)
        </label>
        <div className="rounded-lg px-3 py-2.5 border flex items-center gap-2" style={{ backgroundColor: '#fef3c7', borderColor: '#fde68a' }}>
          <span className="text-sm font-semibold" style={{ color: '#1f2937' }}>
            {fromLocation || (factoryName ? 'Location loading...' : 'Select a factory above first')}
          </span>
          {fromLocation && (
            <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#d97706', color: '#ffffff' }}>
              Auto-filled
            </span>
          )}
        </div>
      </div>
      {/* Destinations */}
      {fromLocation && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold" style={{ color: '#374151' }}>Destinations & Truck Split</p>
            <button
              type="button"
              onClick={addDestination}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
              style={{ backgroundColor: '#d97706', color: '#ffffff' }}
            >
              + Add Destination
            </button>
          </div>

          <div className="space-y-3">
            {destinations?.map((dest, index) => {
              const dist = dest?.lockedDist || null;
              const ratePerTon = dist ? calcRatePerTon(dist) : null;
              const pincodeValid = isPincodeValid(dest?.pincode);
              return (
                <div key={dest?.id} className="rounded-xl border p-4 space-y-3" style={{ backgroundColor: '#ffffff', borderColor: '#e5d9cc' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                      Destination {index + 1}
                    </span>
                    {destinations?.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDestination(dest?.id)}
                        className="text-xs font-medium px-2 py-0.5 rounded"
                        style={{ color: '#dc2626', backgroundColor: '#fee2e2' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {/* Destination / City (optional) */}
                  <div>
                    <label className="text-xs font-medium block mb-1" style={{ color: '#374151' }}>
                      Destination / City <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={dest?.city}
                      onChange={e => updateDestination(dest?.id, 'city', e?.target?.value)}
                      placeholder="e.g. Mumbai, Pune..."
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                      style={{ borderColor: '#e5d9cc', backgroundColor: '#ffffff', color: '#1f2937' }}
                    />
                  </div>
                  {/* Pincode + Trucks */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium block mb-1" style={{ color: '#374151' }}>
                        Pincode <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={dest?.pincode}
                        onChange={e => updateDestination(dest?.id, 'pincode', e?.target?.value)}
                        placeholder="6-digit pincode"
                        className="w-full rounded-lg border px-3 py-2 text-sm font-semibold"
                        style={{
                          borderColor: dest?.pincode && !pincodeValid ? '#dc2626' : pincodeValid ? '#059669' : '#e5d9cc',
                          backgroundColor: '#ffffff',
                          color: '#1f2937'
                        }}
                      />
                      {dest?.pincode && !pincodeValid && (
                        <p className="text-[10px] mt-0.5" style={{ color: '#dc2626' }}>Enter valid 6-digit pincode</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1" style={{ color: '#374151' }}>No. of Trucks</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={dest?.trucks}
                        onChange={e => updateDestination(dest?.id, 'trucks', e?.target?.value)}
                        className="w-full rounded-lg border px-3 py-2 text-sm font-semibold"
                        style={{ borderColor: '#e5d9cc', backgroundColor: '#ffffff', color: '#1f2937' }}
                      />
                    </div>
                  </div>
                  {pincodeValid && dest?.distLoading && (
                    <div className="flex items-center gap-2 py-1">
                      <div className="w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-[10px]" style={{ color: '#9ca3af' }}>Calculating road distance...</p>
                    </div>
                  )}
                  {pincodeValid && !dest?.distLoading && dist !== null && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-lg p-2 text-center" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                        <p className="text-[10px] font-medium" style={{ color: '#9ca3af' }}>Road Distance</p>
                        <p className="text-sm font-bold" style={{ color: '#059669' }}>{dist} km</p>
                      </div>
                      <div className="rounded-lg p-2 text-center" style={{ backgroundColor: '#faf7f2', border: '1px solid #f0e8de' }}>
                        <p className="text-[10px] font-medium" style={{ color: '#9ca3af' }}>Rate per Ton</p>
                        <p className="text-sm font-bold" style={{ color: '#d97706' }}>{ratePerTon ? `₹${ratePerTon?.toLocaleString('en-IN')}` : '—'}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Totals */}
          {totalTrucks > 0 && (
            <div className="rounded-xl p-4 mt-3 space-y-2" style={{ backgroundColor: '#fef3c7', border: '1.5px solid #fde68a' }}>
              <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#92400e' }}>Transport Summary</p>
              <div className="flex justify-between text-sm">
                <span style={{ color: '#6b7280' }}>Total Trucks</span>
                <span className="font-bold" style={{ color: '#1f2937' }}>{totalTrucks} trucks</span>
              </div>
              {totalTransportCost > 0 && (
                <div className="flex justify-between text-sm pt-2 border-t" style={{ borderColor: '#fde68a' }}>
                  <span style={{ color: '#6b7280' }}>Est. Total Transport Cost</span>
                  <span className="text-base font-extrabold" style={{ color: '#d97706' }}>{formatCurrency(totalTransportCost)}</span>
                </div>
              )}
              <div className="mt-2 pt-2 border-t flex items-start gap-2" style={{ borderColor: '#fde68a' }}>
                <Truck size={13} color="#d97706" className="mt-0.5 flex-shrink-0" />
                <p className="text-xs" style={{ color: '#92400e' }}>
                  Final transport rate and exact distance will be confirmed after booking with the transporter.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      {!factoryName && (
        <p className="text-xs italic" style={{ color: '#9ca3af' }}>
          Select a factory in the Order Details section above to auto-fill the loading location.
        </p>
      )}
    </div>
  );
};

const SugarOrderRequestForm = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [sharedFactories, setSharedFactories] = useState([]);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [formData, setFormData] = useState({
    factoryName: '',
    sugarGrade: '',
    qtyRequired: '',
    pricePerTon: '',
    deliveryDate: '',
    deliveryDestination: '',
    lorryNumber: '',
    driverNumber: '',
    specialInstructions: '',
    billingName: '',
    emailId: '',
    includeTruck: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const sugarGradeOptions = [
    { value: 'S1', label: 'S1 - Premium Grade' },
    { value: 'S2', label: 'S2 - Standard Grade' },
    { value: 'M30', label: 'M30 - Industrial Grade' }
  ];

  // Subscribe to shared factories store for live sync
  useEffect(() => {
    const unsub = subscribeFactories((updated) => {
      const list = Array.isArray(updated) ? updated : [];
      setSharedFactories(list?.filter(f => f?.status === 'Active'));
    });
    return unsub;
  }, []);

  // Merge static factoryData with shared factories from Quota Manager
  const factoryOptions = (() => {
    const staticOptions = factoryData?.map(f => ({ value: f?.value, label: f?.label, city: f?.city, location: f?.location }));
    const sharedOptions = sharedFactories
      ?.filter(f => f?.shortCode && !staticOptions?.some(s => s?.value === f?.shortCode))
      ?.map(f => ({
        value: f?.shortCode,
        label: `${f?.shortCode} – ${f?.name}`,
        location: f?.location,
        city: f?.location?.split(',')?.[0]?.trim() || '',
      }));
    return [...staticOptions, ...sharedOptions];
  })();

  const totalAmount = formData?.qtyRequired && formData?.pricePerTon
    ? (parseFloat(formData?.qtyRequired) * parseFloat(formData?.pricePerTon))?.toFixed(2)
    : null;

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.factoryName?.trim()) newErrors.factoryName = 'Factory name is required';
    if (!formData?.sugarGrade) newErrors.sugarGrade = 'Sugar grade is required';
    if (!formData?.qtyRequired) {
      newErrors.qtyRequired = 'Quantity is required';
    } else if (isNaN(formData?.qtyRequired) || parseFloat(formData?.qtyRequired) <= 0) {
      newErrors.qtyRequired = 'Please enter a valid quantity greater than 0';
    }
    if (!formData?.pricePerTon) {
      newErrors.pricePerTon = 'Price per ton is required';
    } else if (isNaN(formData?.pricePerTon) || parseFloat(formData?.pricePerTon) <= 0) {
      newErrors.pricePerTon = 'Please enter a valid price';
    }
    if (!formData?.deliveryDate) {
      newErrors.deliveryDate = 'Delivery date is required';
    } else {
      const selectedDate = startOfDay(parseISO(formData?.deliveryDate));
      const today = startOfDay(new Date());
      if (!isAfter(selectedDate, today) && selectedDate?.getTime() !== today?.getTime()) {
        newErrors.deliveryDate = 'Delivery date cannot be in the past';
      }
    }
    if (!formData?.deliveryDestination?.trim()) newErrors.deliveryDestination = 'Delivery destination is required';
    if (!formData?.lorryNumber?.trim()) newErrors.lorryNumber = 'Lorry number is required';
    if (!formData?.driverNumber) {
      newErrors.driverNumber = 'Driver number is required';
    } else if (!/^[6-9]\d{9}$/?.test(formData?.driverNumber)) {
      newErrors.driverNumber = 'Please enter a valid 10-digit phone number';
    }
    if (!formData?.billingName?.trim()) newErrors.billingName = 'Billing name is required';
    if (!formData?.emailId?.trim()) {
      newErrors.emailId = 'Email ID is required';
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/?.test(formData?.emailId)) {
      newErrors.emailId = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    // If user is not logged in, show guest modal
    if (!isAuthenticated) {
      setShowGuestModal(true);
      return;
    }

    setIsLoading(true);

    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      try {
        await fetch(`${supabaseUrl}/functions/v1/send-sugar-order-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
      } catch (_) {}

      const poNumber = generatePONumber();
      const qty = parseFloat(formData?.qtyRequired);
      const price = parseFloat(formData?.pricePerTon);
      const total = (qty * price)?.toFixed(2);

      const pendingOrder = {
        id: poNumber, poNumber,
        date: new Date()?.toISOString()?.split('T')?.[0],
        submittedAt: new Date()?.toISOString(),
        status: 'Pending Approval',
        factoryName: formData?.factoryName,
        sugarGrade: formData?.sugarGrade,
        qtyRequired: formData?.qtyRequired,
        pricePerTon: formData?.pricePerTon,
        totalAmount: total,
        deliveryDate: formData?.deliveryDate,
        deliveryDestination: formData?.deliveryDestination,
        lorryNumber: formData?.lorryNumber,
        driverNumber: formData?.driverNumber,
        billingName: formData?.billingName,
        emailId: formData?.emailId,
        specialInstructions: formData?.specialInstructions,
        includeTruck: formData?.includeTruck,
      };

      savePendingOrder(pendingOrder);

      setSuccessData({
        poNumber,
        factoryName: formData?.factoryName,
        sugarGrade: formData?.sugarGrade,
        qtyRequired: formData?.qtyRequired,
        totalAmount: total,
        includeTruck: formData?.includeTruck,
      });

      setFormData({
        factoryName: '', sugarGrade: '', qtyRequired: '', pricePerTon: '',
        deliveryDate: '', deliveryDestination: '', lorryNumber: '', driverNumber: '',
        specialInstructions: '', billingName: '', emailId: '', includeTruck: false,
      });
    } catch (error) {
      setErrors({ submit: 'Failed to submit purchase order. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (successData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#f8f1e9' }}>
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-amber-100">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ backgroundColor: '#fef3c7' }}>
            <CheckCircle className="w-12 h-12" style={{ color: '#d97706' }} />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#1f2937' }}>Sugar Purchase Order Form</h2>
          <p className="text-base font-semibold mb-4" style={{ color: '#059669' }}>Submitted Successfully</p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
            <Clock className="w-4 h-4" style={{ color: '#d97706' }} />
            <span className="text-sm font-semibold" style={{ color: '#92400e' }}>Pending Approval</span>
          </div>
          {successData?.includeTruck && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ml-2" style={{ backgroundColor: '#d1fae5', border: '1px solid #6ee7b7' }}>
              <Truck className="w-4 h-4" style={{ color: '#059669' }} />
              <span className="text-sm font-semibold" style={{ color: '#065f46' }}>Truck Requested</span>
            </div>
          )}
          <div className="rounded-xl px-6 py-4 mb-6 border-2" style={{ backgroundColor: '#fffbeb', borderColor: '#f59e0b' }}>
            <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#92400e' }}>Purchase Order Number</p>
            <p className="text-2xl font-bold tracking-wide" style={{ color: '#b45309' }}>{successData?.poNumber}</p>
          </div>
          <div className="rounded-xl p-5 mb-4 text-left space-y-3" style={{ backgroundColor: '#f8f1e9' }}>
            <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: '#6b7280' }}>Order Summary</h3>
            <div className="flex justify-between items-center py-2 border-b border-amber-100">
              <span className="text-sm font-medium" style={{ color: '#6b7280' }}>Factory</span>
              <span className="text-sm font-semibold" style={{ color: '#1f2937' }}>{successData?.factoryName}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-amber-100">
              <span className="text-sm font-medium" style={{ color: '#6b7280' }}>Sugar Grade</span>
              <span className="text-sm font-semibold" style={{ color: '#1f2937' }}>{successData?.sugarGrade}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-amber-100">
              <span className="text-sm font-medium" style={{ color: '#6b7280' }}>Quantity</span>
              <span className="text-sm font-semibold" style={{ color: '#1f2937' }}>{successData?.qtyRequired} Tons</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium" style={{ color: '#6b7280' }}>Total Amount</span>
              <span className="text-base font-bold" style={{ color: '#b45309' }}>
                ₹{parseFloat(successData?.totalAmount)?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
          <p className="text-xs mb-6 px-2" style={{ color: '#9ca3af' }}>
            Your order is under review. Once approved by the platform, it will appear in your <strong>Active Bookings</strong> and Track Shipment will become available.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/buyer-operations-dashboard')} variant="default" size="lg" fullWidth className="font-semibold text-white shadow-md" style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}>
              View in My Bookings
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" size="lg" fullWidth className="font-medium" style={{ color: '#6b7280', borderColor: '#d1d5db' }}>
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Book Sugar — Order Request | SugarHathi</title>
        <meta name="description" content="Place a sugar order request on SugarHathi's marketplace and get connected with verified sugar factories and traders." />
      </Helmet>
      {/* Guest Login Modal */}
      {showGuestModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-7 text-center" style={{ backgroundColor: '#ffffff' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef3c7' }}>
              <Truck size={26} color="#d97706" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1f2937' }}>Login Required</h3>
            <p className="text-sm mb-6" style={{ color: '#6b7280' }}>
              Please login or register to complete your booking.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowGuestModal(false); navigate('/login-authentication'); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ backgroundColor: '#d97706', color: '#ffffff' }}
              >
                Login
              </button>
              <button
                onClick={() => { setShowGuestModal(false); navigate('/user-registration'); }}
                className="flex-1 py-2.5 rounded-xl border text-sm font-bold"
                style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
              >
                Register
              </button>
            </div>
            <button
              onClick={() => setShowGuestModal(false)}
              className="mt-4 text-xs underline"
              style={{ color: '#9ca3af' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#f8f1e9' }}>
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 overflow-hidden shadow-lg ring-2 cursor-pointer" style={{ backgroundColor: '#fff8ed', ringColor: '#f59e0b' }} onClick={() => navigate('/')} title="SugarHathi home">
              <img
                src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png"
                alt="SugarHathi logo - Blue elephant mascot holding sugar cube"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="inline-flex items-center gap-2 mb-2">
              <ClipboardList className="w-6 h-6" style={{ color: '#f59e0b' }} />
              <h1 className="text-3xl font-bold" style={{ color: '#1f2937' }}>Sugar Purchase Order Form</h1>
            </div>
            <p className="text-sm" style={{ color: '#6b7280' }}>Fill in the details below to confirm your sugar purchase order</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 space-y-6 border border-amber-100">

            {/* Section: Order Details */}
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wide mb-4 pb-2 border-b border-amber-100" style={{ color: '#92400e' }}>
                Order Details
              </h2>
              <div className="space-y-5">
                <Select
                  label="Factory Name"
                  options={factoryOptions}
                  value={formData?.factoryName}
                  onChange={(value) => handleChange('factoryName', value)}
                  placeholder="Select factory"
                  error={errors?.factoryName}
                  required
                />
                <Select
                  label="Sugar Grade"
                  options={sugarGradeOptions}
                  value={formData?.sugarGrade}
                  onChange={(value) => handleChange('sugarGrade', value)}
                  placeholder="Select sugar grade"
                  error={errors?.sugarGrade}
                  required
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <QuantityInput
                    label="Quantity Required (Tons)"
                    value={String(formData?.qtyRequired ?? '')}
                    onChange={(val) => handleChange('qtyRequired', val)}
                    onBlur={() => {}}
                    error={errors?.qtyRequired}
                    min={0}
                    max={99999}
                    step={1}
                    placeholder="e.g. 50"
                    suffix="MT"
                    prefix=""
                  />
                  <QuantityInput
                    label="Price Per Ton (₹)"
                    value={String(formData?.pricePerTon ?? '')}
                    onChange={(val) => handleChange('pricePerTon', val)}
                    onBlur={() => {}}
                    error={errors?.pricePerTon}
                    min={0}
                    max={99999}
                    step={100}
                    placeholder="e.g. 3800"
                    prefix="₹"
                    suffix=""
                  />
                </div>
                {totalAmount && (
                  <div className="rounded-lg px-4 py-3 flex items-center justify-between" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                    <span className="text-sm font-medium" style={{ color: '#92400e' }}>Estimated Total Amount</span>
                    <span className="text-base font-bold" style={{ color: '#b45309' }}>
                      ₹{parseFloat(totalAmount)?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Section: Sugar + Truck Option */}
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wide mb-4 pb-2 border-b border-amber-100" style={{ color: '#92400e' }}>
                Transport Option
              </h2>
              <div className="space-y-4">
                {/* Toggle */}
                <div
                  onClick={() => handleChange('includeTruck', !formData?.includeTruck)}
                  className="rounded-xl border p-4 cursor-pointer flex items-center gap-4 transition-all"
                  style={{
                    backgroundColor: formData?.includeTruck ? '#fffbeb' : '#f9fafb',
                    borderColor: formData?.includeTruck ? '#d97706' : '#e5e7eb',
                  }}
                >
                  <div
                    className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                    style={{ borderColor: formData?.includeTruck ? '#d97706' : '#9ca3af', backgroundColor: formData?.includeTruck ? '#d97706' : '#ffffff' }}
                  >
                    {formData?.includeTruck && <span className="text-white text-xs font-bold">✓</span>}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Truck size={16} color={formData?.includeTruck ? '#d97706' : '#6b7280'} />
                      <span className="text-sm font-semibold" style={{ color: formData?.includeTruck ? '#92400e' : '#374151' }}>
                        Include Truck Booking (Sugar + Truck)
                      </span>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
                      Book a verified truck along with your sugar order for accurate rate and route.
                    </p>
                  </div>
                </div>

                {/* Sugar + Truck section */}
                {formData?.includeTruck && (
                  <SugarTruckSection
                    factoryName={formData?.factoryName}
                    formData={formData}
                    onChange={handleChange}
                    allFactories={factoryOptions}
                  />
                )}
              </div>
            </div>

            {/* Section: Delivery Details */}
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wide mb-4 pb-2 border-b border-amber-100" style={{ color: '#92400e' }}>
                Delivery Details
              </h2>
              <div className="space-y-5">
                <Input
                  label="Delivery Required By"
                  type="date"
                  value={formData?.deliveryDate}
                  onChange={(e) => handleChange('deliveryDate', e?.target?.value)}
                  error={errors?.deliveryDate}
                  required
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                />
                <Input
                  label="Delivery Destination"
                  type="text"
                  placeholder="Enter delivery address"
                  value={formData?.deliveryDestination}
                  onChange={(e) => handleChange('deliveryDestination', e?.target?.value)}
                  error={errors?.deliveryDestination}
                  required
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Input
                    label="Lorry Number"
                    type="text"
                    placeholder="e.g. MH12AB1234"
                    value={formData?.lorryNumber}
                    onChange={(e) => handleChange('lorryNumber', e?.target?.value)}
                    error={errors?.lorryNumber}
                    required
                  />
                  <Input
                    label="Driver Contact Number"
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={formData?.driverNumber}
                    onChange={(e) => handleChange('driverNumber', e?.target?.value)}
                    error={errors?.driverNumber}
                    required
                    maxLength="10"
                  />
                </div>
              </div>
            </div>

            {/* Section: Billing & Contact */}
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wide mb-4 pb-2 border-b border-amber-100" style={{ color: '#92400e' }}>
                Billing & Contact
              </h2>
              <div className="space-y-5">
                <Input
                  label="Billing Name"
                  type="text"
                  placeholder="Enter billing name"
                  value={formData?.billingName}
                  onChange={(e) => handleChange('billingName', e?.target?.value)}
                  error={errors?.billingName}
                  required
                />
                <Input
                  label="Email ID"
                  type="email"
                  placeholder="Enter your email address"
                  value={formData?.emailId}
                  onChange={(e) => handleChange('emailId', e?.target?.value)}
                  error={errors?.emailId}
                  required
                />
                <div>
                  <label className="text-sm font-medium block mb-2" style={{ color: '#374151' }}>
                    Special Instructions <span className="text-xs font-normal" style={{ color: '#9ca3af' }}>(Optional)</span>
                  </label>
                  <textarea
                    placeholder="Enter any special instructions or requirements"
                    value={formData?.specialInstructions}
                    onChange={(e) => handleChange('specialInstructions', e?.target?.value)}
                    className="flex min-h-[100px] w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 resize-none"
                    style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', color: '#1f2937' }}
                    rows="3"
                  />
                </div>
              </div>
            </div>

            {errors?.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600 text-sm">
                {errors?.submit}
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isLoading}
              className="font-semibold text-white shadow-md"
              style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
            >
              {isLoading ? 'Processing...' : 'Confirm Purchase Order'}
            </Button>
          </form>

          <p className="text-center text-xs mt-6" style={{ color: '#9ca3af' }}>
            All required fields must be filled accurately before submitting the purchase order.
          </p>
        </div>
      </div>
    </>
  );
};

export default SugarOrderRequestForm;